import React from 'react';
import { 
    Box, Typography, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, IconButton, TextField, InputAdornment, 
    CircularProgress, Checkbox, Tooltip, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import { standardFilterContainerSx, standardSearchInputSx } from '../../components/common/tableStyles';

const PermissionsMatrix = ({ 
    roles, 
    permissionsList, 
    isLoading, 
    searchTerm, 
    setSearchTerm,
    editingRoleId,
    draftPermissions,
    isSaving,
    onStartEdit,
    onCancelEdit,
    onTogglePermission,
    onSaveColumn
}) => {
    const filteredPermissions = permissionsList.filter(p => {
        const search = searchTerm.toLowerCase();
        const keyMatch = p.key ? String(p.key).toLowerCase().includes(search) : false;
        const nameMatch = p.name ? String(p.name).toLowerCase().includes(search) : false;
        const descMatch = p.description ? String(p.description).toLowerCase().includes(search) : false;
        const catMatch = p.category ? String(p.category).toLowerCase().includes(search) : false;
        return keyMatch || nameMatch || descMatch || catMatch;
    });


    return (
        <>
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search permissions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper} sx={{ 
                borderRadius: '12px', 
                border: '1px solid #eaecf0',
                boxShadow: '0 1px 3px 0 rgba(16, 24, 40, 0.05)', 
                maxHeight: '75vh', 
                width: '100%',
                maxWidth: 'calc(100vw - 320px)',
                overflowX: 'auto',
                overflowY: 'auto'
            }}>
                <Table stickyHeader size="small" sx={{ minWidth: 'max-content' }}>
                    <TableHead>
                        <TableRow>
                            {/* Sticky first column header */}
                            <TableCell sx={{
                                position: 'sticky', left: 0, zIndex: 4,
                                bgcolor: '#f8fafc', fontWeight: 600,
                                minWidth: 240, maxWidth: 240, width: 240,
                                borderRight: '1px solid #eaecf0',
                                fontSize: '0.75rem', textTransform: 'none', color: '#475467',
                                boxSizing: 'border-box'
                            }}>
                                Permission
                            </TableCell>
                            {roles.map(role => (
                                <TableCell key={role.id} align="center" sx={{
                                    bgcolor: editingRoleId === role.id ? '#dae6f2' : '#f0f2f5', 
                                    fontWeight: 'bold',
                                    minWidth: 130, zIndex: 3,
                                    borderBottom: editingRoleId === role.id ? '2px solid #1976d2' : '2px solid rgba(25,118,210,0.15)',
                                    transition: 'background-color 0.3s',
                                    boxSizing: 'border-box'
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 0.5 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ maxWidth: 120 }}>
                                            {typeof role.name === 'object' ? (role.name.name || 'Role') : role.name}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {editingRoleId === role.id ? (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => onSaveColumn(role.id)}
                                                        disabled={isSaving}
                                                        startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                                        sx={{ textTransform: 'none', px: 1, minWidth: 'auto', fontSize: '0.75rem', py: 0.25 }}
                                                    >
                                                        {isSaving ? 'Saving' : 'Save'}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={onCancelEdit}
                                                        disabled={isSaving}
                                                        sx={{ textTransform: 'none', px: 1, minWidth: 'auto', fontSize: '0.75rem', py: 0.25 }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => onStartEdit(role)}
                                                    disabled={!!editingRoleId || isSaving}
                                                    startIcon={<EditIcon />}
                                                    sx={{ textTransform: 'none', px: 1, minWidth: 'auto', fontSize: '0.75rem', py: 0.25 }}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPermissions.length > 0 ? (
                            filteredPermissions.map((perm) => (
                                <TableRow 
                                    key={perm.id} 
                                    hover 
                                    sx={{ 
                                        '&:hover td': { bgcolor: 'rgba(25,118,210,0.03)' },
                                        '&:hover td.sticky-permission-cell, &:hover td:first-of-type': { 
                                            bgcolor: '#f4f8fc !important' 
                                        },
                                    }}
                                >
                                    {/* Sticky first column body */}
                                    <TableCell 
                                        className="sticky-permission-cell"
                                        sx={{
                                            position: 'sticky', left: 0, zIndex: 2,
                                            bgcolor: 'background.paper',
                                            borderRight: '2px solid rgba(0,0,0,0.08)',
                                            minWidth: 240, maxWidth: 240, width: 240,
                                            boxSizing: 'border-box',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                bgcolor: '#f4f8fc !important',
                                            }
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" fontFamily="monospace" fontWeight={700} sx={{ color: 'text.primary', fontSize: '0.78rem' }}>
                                                {perm.key || (typeof perm.name === 'object' ? perm.name.name : perm.name)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                                {perm.name && typeof perm.name === 'string' ? perm.name : ''}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    {roles.map(role => {
                                        const permKey = perm.key || (typeof perm.name === 'object' ? perm.name.name : perm.name);
                                        const isEditing = editingRoleId === role.id;
                                        
                                        const rolePerms = (role.permissions || []).map(p => {
                                            if (typeof p === 'string') return p;
                                            return p?.key || p?.name || p?.code || '';
                                        }).filter(Boolean);

                                        const isChecked = isEditing 
                                            ? draftPermissions.includes(permKey)
                                            : rolePerms.includes(permKey);
                                        
                                        return (
                                            <TableCell
                                                key={`${role.id}-${perm.id}`}
                                                align="center"
                                                sx={{
                                                    bgcolor: isEditing ? 'rgba(25,118,210,0.04)' : (isChecked ? 'rgba(0,0,0,0.02)' : 'transparent'),
                                                    transition: 'background-color 0.2s',
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={isChecked}
                                                    onChange={() => onTogglePermission(permKey)}
                                                    disabled={isSaving || !isEditing}
                                                    sx={{
                                                        color: isEditing ? 'primary.main' : 'rgba(0,0,0,0.15)',
                                                        '&.Mui-checked': { 
                                                            color: isEditing ? 'primary.main' : 'rgba(0,0,0,0.4)' 
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={roles.length + 1} align="center" sx={{ py: 8 }}>
                                    {isLoading
                                        ? <CircularProgress />
                                        : <Typography variant="body1" color="text.secondary">No permissions found.</Typography>
                                    }
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default PermissionsMatrix;
